/**
 * PultBoard — the board of the pult, part for part, and the hand on its controls (task 54; variant
 * B since 2026-11-03, `.omc/wiki/pult-variant-b.md`).
 *
 * `docs/superpowers/specs/2026-10-05-pult-design.md` «Экран, раскладка, поворот». Every part of
 * `lib/pult-layout.ts` is one absolutely placed element in points of the board (`--x`, `--y`,
 * `--w`, `--h`, `--fs`), and `styles/pult.css` turns points into shares of the board, as
 * `deck.css` does with millimetres: the board scales as one picture to fit the screen whole, and
 * no part moves against another.
 *
 * ## Tabs
 *
 * Each deck has VINYL · PADS · LOOP, the mixer MIXER · FX · SCREEN; a tab pressed shows its parts
 * in the place of the last one's (`onTab`, the tabs chosen in `tabs`). Every part of every tab stays
 * in the page, its element and its state kept: a part of a tab not chosen is `hidden` — out of
 * sight, out of a finger's reach and a screen reader's — and the tests of the sound find every
 * control by its name whatever the tab. A finger on a control as its tab goes has no lift to come:
 * the control lets go as it hides, the way a lift lets it go (`useLetGoHidden`) — a ROLL pad's
 * roll, a knob or a fader back to the automat, a platter's scratch. The parts are keyed by their
 * id, never by their place in the list: a finger's state stays with its own control through a turn
 * of the phone.
 *
 * ## Every finger is its own press
 *
 * Each control is an element with its own Pointer Events, and it keeps the `pointerId` of each
 * finger on it (`setPointerCapture`): two fingers on two pads are two presses, and a finger that
 * slides off a knob still turns it. A key acts as the finger lands — a pult's keys do — and a
 * keyboard's Enter does the same through `click` (`detail === 0`), so a tap is never two presses.
 *
 * - a key, a pad: pressed while held; held {@link PAD_HOLD_MS} — its second action, where the
 *   device had SHIFT (task 60: a HOT CUE taken away; task 62: RELOOP);
 * - a knob: the finger's travel up and down, one point of the screen a hundredth of the turn;
 * - a fader, the crossfader: the cap goes with the finger by as much as it moved, from where it
 *   stood — taking a fader never jumps it to the finger;
 * - a paddle: up, and down again;
 * - a platter: the turntable's own gesture, on its own deck (task 57, {@link Platter}).
 *
 * There is no magnifier and no sleeping board any more (`.omc/wiki/pult-variant-b.md`): every
 * finger on the board is a control's, or nothing's.
 *
 * ## What the owner missed on the iPhone (2026-11-04)
 *
 * «не попасть проценты не меняютя нет ощущения когда фейдер двигаешь что на ноль пришел»: RESET
 * says its deck's tempo over its word, «+1,2 %», and «0 %» at zero; a «0» by the tempo fader marks
 * zero's place and lights while the tempo is zero, and the finger that brings the fader there feels
 * the click, as on the turntable. «когда трек инрает пластинки не крутяться»: a deck's record turns
 * while the deck plays ({@link Platter}). «если кликаю в середину яблоко пластинки меняется»: its
 * middle is the turntable's face, and a tap draws the next.
 *
 * What a key does is the screen's (`onKey`, `onKeyUp`, and a pad's long press `onKeyHold` —
 * `screens/Pult.tsx`). A knob or a fader moves a value of `store/pult.ts`, and a deck's tempo
 * fader that deck's tempo (task 55); tasks 56–58 give the other values their sound.
 */

import type { CSSProperties, JSX, KeyboardEvent, MouseEvent, PointerEvent, ReactNode } from 'react';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { player } from '../engine/player';
import { scratch } from '../engine/scratch';
import type { ScratchHand } from '../engine/scratch';
import { RETURN_MS, RETURN_STEPS } from '../engine/speed';
import { formatPitch } from '../lib/format';
import { adapterFor, nextFace } from '../lib/labels';
import type { LabelKind } from '../lib/labels';
import { createPlatterGesture } from '../lib/platter';
import type { PlatterGesture } from '../lib/platter';
import { PAD_COLOURS, ZERO_FS, faderCap, faderZero } from '../lib/pult-layout';
import { PAD_HOLD_MS } from '../lib/pult-pads';
import type {
  DeckSide,
  PultBoardLayout,
  PultFader,
  PultPart,
  PultRect,
  PultSide,
  PultTab,
} from '../lib/pult-layout';
import { TURN_S, nominalDegPerS } from '../lib/records';
import type { RecordSize } from '../lib/records';
import { telegram } from '../lib/telegram';
import {
  PITCH_RANGE,
  leaveDeck,
  setFace,
  setPitch,
  takeDeck,
  tempoRate,
  useDeck,
} from '../store/deck';
import { chooseTab, setHeld, setValue, usePult, valueOf } from '../store/pult';
import type { PultTabs } from '../store/pult';
// The record's face and its turn are the turntable's own: its labels' lines and `record-spin`.
import '../styles/deck.css';

import { Cover } from './Cover';
import { ADAPTER_ART } from './labelArt';
import { turnAt, useDeckTurn, useReducedMotion } from './motion';
import { RecordLabel } from './RecordLabel';
import { TAP_MS, TAP_SLOP_PX } from './Vinyl';

/** What the controls are called for a screen reader and in tests: the print, or what it means. */
const NAMES: Readonly<Record<string, string>> = Object.freeze({
  'jump.back': 'BEAT JUMP назад',
  'jump.fwd': 'BEAT JUMP вперёд',
  jumpsize: 'JUMP SIZE',
  slip: 'SLIP',
  censor: 'CENSOR',
  keylock: 'KEYLOCK',
  platter: 'пластинка',
  rpm33: '33',
  rpm45: '45',
  reset: 'RESET',
  pitch: 'темп',
  sync: 'SYNC',
  cue: 'CUE',
  play: 'START·STOP',
  'mode.hotcue': 'режим HOT CUE',
  'mode.roll': 'режим ROLL',
  'mode.sampler': 'режим SAMPLER',
  'mode.loop': 'режим LOOP',
  'param.prev': 'PARAMETER назад',
  'param.next': 'PARAMETER вперёд',
  'bend.down': 'PITCH BEND −',
  'bend.up': 'PITCH BEND +',
  loop: 'LOOP ON/OFF',
  'loop.half': 'петля ½',
  'loop.double': 'петля 2×',
  pullup: 'PULL UP',
  echoout: 'ECHO OUT',
  level: 'LEVEL',
  high: 'HIGH',
  mid: 'MID',
  low: 'LOW',
  filter: 'FILTER',
  fx: 'рычаг FX',
  fader: 'фейдер',
  'load.A': 'LOAD на деку A',
  'load.B': 'LOAD на деку B',
  'screen.queue': 'экран QUEUE',
  'screen.library': 'экран LIBRARY',
  'screen.view': 'экран VIEW',
  'fx.echo': 'эффект ECHO',
  'fx.reverb': 'эффект REVERB',
  'fx.flanger': 'эффект FLANGER',
  'fx.phaser': 'эффект PHASER',
  'fx.beats': 'BEATS',
  'fx.depth': 'DEPTH',
  auto: 'AUTO',
  preset: 'PRESET',
  xfader: 'кроссфейдер',
});

/** The name of a control: «Дека A: KEYLOCK», «пад 3» on its deck, «кроссфейдер». */
function controlName(id: string, side: PultSide): string {
  if (side === 'mid') return NAMES[id] ?? id;
  const own = id.slice(2);
  const pad = /^pad(\d)$/u.exec(own);
  return `Дека ${side}: ${pad === null ? (NAMES[own] ?? own) : `пад ${pad[1] ?? ''}`}`;
}

/** The deck whose `name` control `id` is — `A.pitch` is deck A's tempo fader — or none. */
function ownDeck(id: string, name: string): DeckSide | undefined {
  if (id === `A.${name}`) return 'A';
  if (id === `B.${name}`) return 'B';
  return undefined;
}

/** A tab's name: «Дека A: вкладка PADS», «Микшер: вкладка FX». */
function tabName(group: PultSide, text: string): string {
  return `${group === 'mid' ? 'Микшер' : `Дека ${group}`}: вкладка ${text}`;
}

/**
 * What a deck shows of its own: its record, and the words of its pads, two to a cell — the upper
 * pad's over the lower's, as the copy's screen over the pads had them; each pad shows its own.
 */
export interface PultDeckLights {
  readonly record: RecordSize;
  readonly cells: readonly (readonly [string, string])[];
}

/** What lights the board: the app's state, read by the screen and handed down. */
export interface PultLights {
  readonly decks: Readonly<Record<DeckSide, PultDeckLights>>;
  /** Ids of the keys lit now: START·STOP of a deck that plays, the effect chosen, AUTO, … */
  readonly lit: ReadonlySet<string>;
}

export interface PultBoardProps {
  readonly layout: PultBoardLayout;
  readonly lights: PultLights;
  /** A key pressed. */
  readonly onKey: (id: string) => void;
  /** Task 55: the last finger left a key — PITCH BEND lets go. */
  readonly onKeyUp: (id: string) => void;
  /** Task 60: a finger stayed on a key or a pad {@link PAD_HOLD_MS} — its second action. */
  readonly onKeyHold: (id: string) => void;
  /** What the screen in the middle shows. */
  readonly screen: ReactNode;
  /** Task 56: MAIN's level now, 0…1 — read by the meter while the board is drawn. */
  readonly level: () => number;
  /** The tab each part shows; the store's when not given (`store/pult.ts`). */
  readonly tabs?: PultTabs | undefined;
  /** A tab pressed; the store's `chooseTab` when not given. */
  readonly onTab?: ((group: PultSide, tab: PultTab) => void) | undefined;
}

function at(rect: PultRect, extra: Readonly<Record<string, number | string>> = {}): CSSProperties {
  return { '--x': rect.x, '--y': rect.y, '--w': rect.w, '--h': rect.h, ...extra } as CSSProperties;
}

/** Takes the finger for the element, so it stays this control's off the edge. */
function capture(event: PointerEvent<HTMLElement>): void {
  try {
    event.currentTarget.setPointerCapture(event.pointerId);
  } catch {
    // A webview without pointer capture: the control holds the finger while it stays on it.
  }
}

/**
 * A control hidden by a tab while a finger is on it: the lift will never reach it, so it lets go
 * now, by `letGo` — the lift's own way. A control with nothing held lets go of nothing.
 */
function useLetGoHidden(shown: boolean, letGo: () => void): void {
  const latest = useRef(letGo);
  useEffect(() => {
    latest.current = letGo;
  });
  useEffect(() => {
    if (!shown) latest.current();
  }, [shown]);
}

/* ── keys and pads ─────────────────────────────────────────────────────────── */

/**
 * A key's or a pad's fingers. `onHold` (task 60): a finger stayed {@link PAD_HOLD_MS} — never after
 * the last finger left, nor once the control is gone or hidden, whose lift may never come.
 */
function usePress(
  shown: boolean,
  onPress: () => void,
  onRelease: () => void = () => undefined,
  onHold?: () => void,
) {
  const fingers = useRef(new Set<number>());
  const [pressed, setPressed] = useState(false);
  const hold = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const stopHold = useCallback((): void => {
    if (hold.current !== undefined) clearTimeout(hold.current);
    hold.current = undefined;
  }, []);
  useEffect(() => stopHold, [stopHold]);
  const release = (event: PointerEvent<HTMLElement>): void => {
    if (!fingers.current.delete(event.pointerId)) return;
    if (fingers.current.size > 0) return;
    stopHold();
    setPressed(false);
    onRelease();
  };
  useLetGoHidden(shown, () => {
    if (fingers.current.size === 0) return;
    fingers.current.clear();
    stopHold();
    setPressed(false);
    onRelease();
  });
  return {
    pressed,
    handlers: {
      onPointerDown: (event: PointerEvent<HTMLElement>): void => {
        capture(event);
        fingers.current.add(event.pointerId);
        setPressed(true);
        onPress();
        if (onHold === undefined) return;
        stopHold();
        hold.current = setTimeout(() => {
          hold.current = undefined;
          onHold();
        }, PAD_HOLD_MS);
      },
      onPointerUp: release,
      onPointerCancel: release,
      onClick: (event: MouseEvent<HTMLElement>): void => {
        // A tap has acted as it landed; this is Enter or Space on a keyboard — a press and its
        // release at once, so a key held by a finger (PITCH BEND) is not left held.
        if (event.detail !== 0) return;
        onPress();
        onRelease();
      },
    },
  };
}

/**
 * RESET's own line: its deck's tempo now, as the turntable writes it (`formatPitch`) — and at zero
 * the table's print, «0 %», as the key read before. Its own subscription to the tempo: only these
 * two keys draw again as it moves, not every key of the board.
 */
function DeckTempo({
  side,
  zero,
}: {
  readonly side: DeckSide;
  readonly zero: string;
}): JSX.Element {
  const tempo = useDeck((s) => s.decks[side].pitch);
  return <span className="ppTempo">{tempo === 0 ? zero : formatPitch(tempo)}</span>;
}

const Key = memo(function Key({
  part,
  shown,
  lit,
  onKey,
  onKeyUp,
  onKeyHold,
}: {
  readonly part: Extract<PultPart, { kind: 'key' }>;
  readonly shown: boolean;
  readonly lit: boolean;
  readonly onKey: (id: string) => void;
  readonly onKeyUp: (id: string) => void;
  readonly onKeyHold: (id: string) => void;
}): JSX.Element {
  const { pressed, handlers } = usePress(
    shown,
    () => {
      onKey(part.id);
    },
    () => {
      onKeyUp(part.id);
    },
    () => {
      onKeyHold(part.id);
    },
  );
  const tempoOf = ownDeck(part.id, 'reset');
  return (
    <button
      type="button"
      className={`pp ppKey look-${part.look}${part.tight === true ? ' ppTight' : ''}`}
      style={at(part.rect, { '--fs': part.fs })}
      hidden={!shown}
      aria-label={controlName(part.id, part.side)}
      aria-pressed={part.toggle === true ? lit : undefined}
      data-lit={lit ? 'true' : 'false'}
      data-pressed={pressed ? 'true' : 'false'}
      {...handlers}
    >
      {part.glyph === undefined ? (
        <>
          {tempoOf === undefined ? part.text : <DeckTempo side={tempoOf} zero={part.text} />}
          {part.sub !== undefined && <small>{part.sub}</small>}
        </>
      ) : (
        <svg viewBox="0 0 10 10" aria-hidden="true" className="ppGlyph">
          <polygon points={part.glyph === 'left' ? '7,1.5 7,8.5 2,5' : '3,1.5 3,8.5 8,5'} />
        </svg>
      )}
    </button>
  );
});

const Pad = memo(function Pad({
  part,
  shown,
  word,
  lit,
  onKey,
  onKeyUp,
  onKeyHold,
}: {
  readonly part: Extract<PultPart, { kind: 'pad' }>;
  readonly shown: boolean;
  /** What the copy's screen over the pads said of this pad: its number and cue, a length. */
  readonly word: string;
  readonly lit: boolean;
  readonly onKey: (id: string) => void;
  readonly onKeyUp: (id: string) => void;
  readonly onKeyHold: (id: string) => void;
}): JSX.Element {
  const { pressed, handlers } = usePress(
    shown,
    () => {
      onKey(part.id);
    },
    () => {
      onKeyUp(part.id);
    },
    () => {
      onKeyHold(part.id);
    },
  );
  const [c, hi, lo, g] = PAD_COLOURS[part.colour] ?? PAD_COLOURS[0] ?? ['', '', '', ''];
  return (
    <button
      type="button"
      className="pp ppPad"
      style={at(part.rect, { '--c': c, '--hi': hi, '--lo': lo, '--g': g, '--fs': part.fs })}
      hidden={!shown}
      aria-label={controlName(part.id, part.side)}
      data-lit={lit ? 'true' : 'false'}
      data-pressed={pressed ? 'true' : 'false'}
      {...handlers}
    >
      {word}
    </button>
  );
});

/** Pad `n`'s word in a deck's cells: the upper row's pads over the lower's. */
function padWord(cells: PultDeckLights['cells'], id: string): string {
  const n = Number(/pad(\d)$/u.exec(id)?.[1] ?? 0);
  return cells[(n - 1) % 4]?.[n > 4 ? 1 : 0] ?? '';
}

/* ── tabs ──────────────────────────────────────────────────────────────────── */

/** A tab: chosen as the finger lands, as a key acts; lit while it is the one shown. */
const Tab = memo(function Tab({
  part,
  chosen,
  onTab,
}: {
  readonly part: Extract<PultPart, { kind: 'tab' }>;
  readonly chosen: boolean;
  readonly onTab: (group: PultSide, tab: PultTab) => void;
}): JSX.Element {
  const choose = (): void => {
    onTab(part.group, part.tab);
  };
  return (
    <button
      type="button"
      role="tab"
      className="pp ppTab"
      style={at(part.rect, { '--fs': part.fs })}
      aria-label={tabName(part.group, part.text)}
      aria-selected={chosen}
      data-lit={chosen ? 'true' : 'false'}
      onPointerDown={choose}
      onClick={(event) => {
        if (event.detail !== 0) return;
        choose();
      }}
    >
      {part.text}
    </button>
  );
});

/* ── knobs, faders, paddles ────────────────────────────────────────────────── */

/**
 * Where a knob or a fader stands, 0…1, and how to move it: the pult's own value
 * (`store/pult.ts`), or — a deck's tempo fader, since task 55 — that deck's tempo, the top of the
 * slot «−8 %» and its foot «+8 %», as printed beside it.
 */
function useControl(id: string): readonly [number, (value: number) => void] {
  const side = ownDeck(id, 'pitch');
  const tempo = useDeck((s) => (side === undefined ? 0 : s.decks[side].pitch));
  const own = usePult((s) => valueOf(s.values, id));
  if (side === undefined) {
    return [
      own,
      (value) => {
        setValue(id, value);
      },
    ];
  }
  return [
    (tempo + PITCH_RANGE) / (2 * PITCH_RANGE),
    (value) => {
      const before = useDeck.getState().decks[side].pitch;
      setPitch(Math.min(1, Math.max(0, value)) * 2 * PITCH_RANGE - PITCH_RANGE, side);
      // The click into zero is felt, as the turntable's (task 50) — once, as it clicks in, not at
      // every move that stays there.
      if (before !== 0 && useDeck.getState().decks[side].pitch === 0) telegram.haptic('selection');
    },
  ];
}

/**
 * A knob's or a fader's finger: where it came down and the value then, kept until it leaves.
 * `travel` turns the finger's way from there into the new value; `undefined` moves nothing.
 */
function useDrag(
  id: string,
  shown: boolean,
  value: number,
  write: (value: number) => void,
  travel: (dx: number, dy: number) => number | undefined,
) {
  const drag = useRef<{ pointerId: number; from: number; x: number; y: number } | undefined>(
    undefined,
  );
  const end = (event: PointerEvent<HTMLElement>): void => {
    if (drag.current?.pointerId !== event.pointerId) return;
    drag.current = undefined;
    // Task 56: let go — the automat's again from its next write.
    setHeld(id, false);
  };
  useLetGoHidden(shown, () => {
    if (drag.current === undefined) return;
    drag.current = undefined;
    setHeld(id, false);
  });
  return {
    onPointerDown: (event: PointerEvent<HTMLElement>): void => {
      capture(event);
      // Task 56: under a finger — the automat writes nothing here.
      setHeld(id, true);
      drag.current = {
        pointerId: event.pointerId,
        from: value,
        x: event.clientX,
        y: event.clientY,
      };
    },
    onPointerMove: (event: PointerEvent<HTMLElement>): void => {
      const held = drag.current;
      if (held?.pointerId !== event.pointerId) return;
      const moved = travel(event.clientX - held.x, event.clientY - held.y);
      if (moved !== undefined) write(held.from + moved);
    },
    onPointerUp: end,
    onPointerCancel: end,
    // The system took the finger: it is gone as surely as lifted.
    onLostPointerCapture: end,
    // Arrow keys move it by a twentieth.
    onKeyDown: (event: KeyboardEvent<HTMLElement>): void => {
      const step =
        event.key === 'ArrowUp' || event.key === 'ArrowRight'
          ? 0.05
          : event.key === 'ArrowDown' || event.key === 'ArrowLeft'
            ? -0.05
            : 0;
      if (step === 0) return;
      event.preventDefault();
      write(value + step);
    },
  };
}

const Knob = memo(function Knob({
  part,
  shown,
}: {
  readonly part: Extract<PultPart, { kind: 'knob' }>;
  readonly shown: boolean;
}): JSX.Element {
  const [value, write] = useControl(part.id);
  // One point of the screen is a hundredth of the turn; up turns it right.
  const handlers = useDrag(part.id, shown, value, write, (_, dy) => -dy / 100);
  // A filter's ring is lit while it filters: off the middle of its travel.
  const resting = part.id.endsWith('filter') && Math.abs(value - 0.5) < 0.02;
  return (
    <div
      className={`pp ppKnob look-${part.look}`}
      style={at(part.rect, { '--turn': `${String(Math.round((value - 0.5) * 270))}deg` })}
      hidden={!shown}
      role="slider"
      tabIndex={0}
      aria-label={controlName(part.id, part.side)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value * 100)}
      data-lit={resting ? 'false' : 'true'}
      {...handlers}
    />
  );
});

const Fader = memo(function Fader({
  part,
  shown,
}: {
  readonly part: PultFader;
  readonly shown: boolean;
}): JSX.Element {
  const [value, write] = useControl(part.id);
  const travel = useRef<HTMLDivElement | null>(null);
  const vertical = part.axis === 'v';
  const handlers = useDrag(part.id, shown, value, write, (dx, dy) => {
    // The cap's way as it is on screen now, at whatever scale the board fits the screen: a finger
    // moved along it moves the cap as far (`faderCap`).
    const box = travel.current?.getBoundingClientRect();
    const length = vertical ? box?.height : box?.width;
    if (length === undefined || length <= 0) return undefined;
    return (vertical ? dy : dx) / length;
  });
  // The touch area is the whole slot, as broad as the cap and never under a finger's 44.
  const area = part.area;
  const local = (rect: PultRect): CSSProperties =>
    at({ x: rect.x - area.x, y: rect.y - area.y, w: rect.w, h: rect.h });
  // A tempo fader's «0»: lit while its deck's tempo is zero. Over the cap, and no target of its
  // own — a finger on it is on the fader.
  const tempoOf = ownDeck(part.id, 'pitch');
  const zero = tempoOf === undefined ? undefined : faderZero(part);
  const atZero = useDeck((s) => tempoOf !== undefined && s.decks[tempoOf].pitch === 0);
  return (
    <div
      className={`pp ppFader axis-${part.axis}`}
      style={at(area)}
      hidden={!shown}
      role="slider"
      tabIndex={0}
      aria-label={controlName(part.id, part.side)}
      aria-orientation={vertical ? 'vertical' : 'horizontal'}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(value * 100)}
      {...handlers}
    >
      {part.ticks !== undefined && <div className="pp ppTicks" style={local(part.ticks)} />}
      <div className="pp ppSlot" style={local(part.slot)} />
      <div ref={travel} className="pp ppTravel" style={local(part.travel)} />
      <div className="pp ppCap" style={local(faderCap(part, value))} />
      {zero !== undefined && (
        <div
          className="pp ppZero"
          style={{ ...local(zero), '--fs': ZERO_FS } as CSSProperties}
          data-lit={atZero ? 'true' : 'false'}
          aria-hidden="true"
        >
          0
        </div>
      )}
    </div>
  );
});

const Paddle = memo(function Paddle({
  part,
  shown,
}: {
  readonly part: Extract<PultPart, { kind: 'paddle' }>;
  readonly shown: boolean;
}): JSX.Element {
  const up = usePult((s) => valueOf(s.values, part.id) >= 0.5);
  const flip = (): void => {
    setValue(part.id, up ? 0 : 1);
  };
  return (
    <button
      type="button"
      className="pp ppPaddle"
      style={at(part.rect)}
      hidden={!shown}
      aria-label={controlName(part.id, part.side)}
      aria-pressed={up}
      data-lit={up ? 'true' : 'false'}
      onPointerDown={(event) => {
        capture(event);
        flip();
      }}
      onClick={(event) => {
        if (event.detail !== 0) return;
        flip();
      }}
    />
  );
});

/* ── the platter ───────────────────────────────────────────────────────────── */

/** A finger on a deck's platter, while it is there. */
interface PlatterGrip {
  readonly pointerId: number;
  readonly hand: ScratchHand | undefined;
  readonly gesture: PlatterGesture;
  readonly releaseSwipes: () => void;
  angle: number;
  movedAt: number;
  /** Where and when the finger landed, and whether on the record itself: is it a tap? */
  readonly landed: { readonly x: number; readonly y: number; readonly at: number };
  readonly onRecord: boolean;
  /** The finger has gone further than {@link TAP_SLOP_PX} from where it landed. */
  strayed: boolean;
}

/**
 * Each record across, as a share of its platter (`styles/pult.css`): the 33 is its mat, inset
 * 7,8 % all round; the 45, `.ppRecord45`. A tap on the felt round the small one is no tap of the
 * record, as on the turntable.
 */
const RECORD_SHARE: Readonly<Record<RecordSize, number>> = { '33': 0.844, '45': 0.4895 };

/**
 * A deck's platter, and a finger on it — task 57: the turntable's gesture on its own deck
 * (`components/Vinyl.tsx`). The scratch first, by the deck's own voice (`engine/scratch.ts`); a
 * track not ready, task 09's table on the deck's speed. The hand is in the deck's own slice, one
 * finger on a platter — and any finger: the first on the glass may be on a fader, so a second one
 * is a platter's too, and two platters are two hands. Everything a touch takes is given back on
 * each way out: the lift, the cancel, the lost capture, the board unmounted under it, its tab
 * changed under it, «уменьшение движения» switched on.
 *
 * Two turns stacked, as on the turntable (`styles/deck.css`): the record turns by itself while its
 * deck plays — at 33⅓ or 45 and at the deck's tempo, standing where it is on a pause, still under
 * «уменьшение движения» — and round it the hand's angle, `--grab`. Under a finger, and while the
 * scratch's voice draws the record after it (`data-held`, `data-voiced`), the record's own turn
 * stands and the hand's carries it; let go, the own turn goes on from where it stood, so the record
 * never jumps. The owner, 2026-11-04: «когда трек инрает пластинки не крутяться не понятно что
 * играет только на слух».
 *
 * Under the scratch the record is the voice's picture, as on the turntable — a record drawn from
 * the finger ran ahead of the sound (`engine/scratch.ts`, «What the record is drawn from»); the
 * finger draws it only on task 09's table.
 *
 * Its middle is the deck's face (`store/deck.ts`): the active deck's is the turntable's, so a
 * change on either is seen on both; a tap — a finger that lands on the record and lifts within
 * {@link TAP_MS} without straying {@link TAP_SLOP_PX} — draws the next, as on the turntable; a
 * finger that moved is a scratch, one that stayed a hold.
 */
function Platter({
  part,
  shown,
  size,
}: {
  readonly part: Extract<PultPart, { kind: 'platter' }>;
  readonly shown: boolean;
  readonly size: RecordSize;
}): JSX.Element {
  const side = part.side === 'B' ? 'B' : 'A';
  // A phone that asked not to be moved gets no gesture, as on the turntable (`components/motion.ts`).
  const still = useReducedMotion();
  const frame = useRef<HTMLDivElement | null>(null);
  const spin = useRef<HTMLDivElement | null>(null);
  const disc = useRef<HTMLDivElement | null>(null);
  const grip = useRef<PlatterGrip | undefined>(undefined);
  const rest = useRef(0);
  const drawing = useRef<number | undefined>(undefined);
  const spinUp = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const playing = useDeck((s) => s.decks[side].playing);
  const tempo = useDeck((s) => s.decks[side].pitch);
  const face = useDeck((s) => s.decks[side].face);
  const cover = useDeck((s) => s.decks[side].track?.cover ?? null);
  const title = useDeck((s) => s.decks[side].track?.title ?? '');
  const performer = useDeck((s) => s.decks[side].track?.performer ?? '');
  const turning = useMemo(() => [disc], []);
  // 45 became 33, or the tempo moved: the record turns on from its angle, at the new pace.
  useDeckTurn(turning, size, tempo);

  const stopSpinUp = useCallback((): void => {
    if (spinUp.current !== undefined) clearInterval(spinUp.current);
    spinUp.current = undefined;
  }, []);

  const stopDrawing = useCallback((): void => {
    if (drawing.current !== undefined) cancelAnimationFrame(drawing.current);
    drawing.current = undefined;
  }, []);

  const letGo = useCallback((): PlatterGrip | undefined => {
    const current = grip.current;
    if (current === undefined) return undefined;
    grip.current = undefined;
    // The swipes first: whatever else fails on the way out, the hold must not outlive the touch.
    current.releaseSwipes();
    current.gesture.end();
    if (current.hand === undefined) player().speedOf(side)?.release();
    else current.hand.release();
    leaveDeck('record', side);
    rest.current = current.angle;
    if (frame.current !== null) delete frame.current.dataset.held;
    return current;
  }, [side]);

  useEffect(
    () => () => {
      stopSpinUp();
      stopDrawing();
      letGo();
    },
    [letGo, stopDrawing, stopSpinUp],
  );
  useEffect(() => {
    if (still) letGo();
  }, [still, letGo]);
  useLetGoHidden(shown, letGo);

  /**
   * The record's own turn after task 09's table, from standing to the deck's pace over
   * {@link RETURN_MS} — the time its sound takes to come back to its tempo, as on the turntable.
   */
  function spinUpDisc(): void {
    stopSpinUp();
    const nodes = [disc.current];
    const full = tempoRate(useDeck.getState().decks[side].pitch);
    let step = 0;
    turnAt(nodes, 0);
    spinUp.current = setInterval(() => {
      step += 1;
      if (step >= RETURN_STEPS) {
        stopSpinUp();
        turnAt(nodes, full);
        return;
      }
      turnAt(nodes, (full * step) / RETURN_STEPS);
    }, RETURN_MS / RETURN_STEPS);
  }

  /** The record turned by the deck's voice every frame: `fromAngle` where the voice was at `fromS`. */
  function drawFromVoice(fromAngle: number, fromS: number): void {
    stopDrawing();
    if (frame.current !== null) frame.current.dataset.voiced = 'true';
    const turn = TURN_S[size];
    const draw = (): void => {
      const atS = scratch().voiceAt(undefined, side);
      if (atS === undefined) {
        drawing.current = undefined;
        // The deck has its sound again: the record's own turn goes on from where it stood.
        if (frame.current !== null) delete frame.current.dataset.voiced;
        return;
      }
      const angle = fromAngle + ((atS - fromS) / turn) * 360;
      rest.current = angle;
      if (grip.current !== undefined) grip.current.angle = angle;
      spin.current?.style.setProperty('--grab', `${String(angle)}deg`);
      drawing.current = requestAnimationFrame(draw);
    };
    draw();
  }

  function onPointerDown(event: PointerEvent<HTMLDivElement>): void {
    // A finger, or the main button of a mouse; one on a platter at a time.
    if (still || grip.current !== undefined || event.button !== 0) return;
    const box = event.currentTarget.getBoundingClientRect();
    if (box.width <= 0 || box.height <= 0) return;
    if (!takeDeck('record', side)) return;
    stopSpinUp();
    // A touch during the voice's return takes the record where the voice has drawn it.
    stopDrawing();
    const hand = scratch().grab(event.timeStamp, side);
    const start = rest.current;
    const centre = { x: box.left + box.width / 2, y: box.top + box.height / 2 };
    const current: PlatterGrip = {
      pointerId: event.pointerId,
      hand,
      angle: start,
      movedAt: event.timeStamp,
      landed: { x: event.clientX, y: event.clientY, at: event.timeStamp },
      onRecord:
        Math.hypot(event.clientX - centre.x, event.clientY - centre.y) <=
        (box.width * RECORD_SHARE[size]) / 2,
      strayed: false,
      releaseSwipes: telegram.holdVerticalSwipes(),
      gesture: createPlatterGesture({
        // The deck's speed asked for each time: the active deck's may change under the finger.
        onRate: (rate) => {
          if (hand === undefined) player().speedOf(side)?.set(rate);
        },
        nominalDegPerS: nominalDegPerS(size),
        onTurn: (travel) => {
          if (hand !== undefined) {
            hand.turn(travel, current.movedAt);
            return;
          }
          current.angle = start + travel;
          spin.current?.style.setProperty('--grab', `${String(current.angle)}deg`);
        },
        now: () => performance.now(),
      }),
    };
    grip.current = current;
    event.currentTarget.dataset.held = 'true';
    capture(event);
    if (hand === undefined) player().speedOf(side)?.touch();
    else drawFromVoice(start, scratch().voiceAt(undefined, side) ?? 0);
    current.gesture.down({ x: event.clientX, y: event.clientY }, centre);
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>): void {
    const current = grip.current;
    if (current?.pointerId !== event.pointerId) return;
    current.movedAt = event.timeStamp;
    const { landed } = current;
    if (Math.hypot(event.clientX - landed.x, event.clientY - landed.y) > TAP_SLOP_PX) {
      current.strayed = true;
    }
    current.gesture.move({ x: event.clientX, y: event.clientY });
  }

  function onPointerEnd(event: PointerEvent<HTMLDivElement>): PlatterGrip | undefined {
    if (grip.current?.pointerId !== event.pointerId) return undefined;
    const left = letGo();
    // Under the scratch the voice goes on drawing the record; task 09's record spins up by itself.
    if (left !== undefined && left.hand === undefined) spinUpDisc();
    return left;
  }

  function onPointerUp(event: PointerEvent<HTMLDivElement>): void {
    const left = onPointerEnd(event);
    // A tap on the record: the next face, drawn as the turntable draws it.
    if (
      left !== undefined &&
      left.onRecord &&
      !left.strayed &&
      event.timeStamp - left.landed.at <= TAP_MS
    ) {
      setFace(side, nextFace(cover !== null, useDeck.getState().decks[side].face));
    }
    // A lift is a tap to the browser: the sound of the scratch wakes here if it slept.
    scratch().prime();
  }

  /** The face in the middle, at the size the pult's own label had: a label, or the cover. */
  const middle = `ppLabel${size}`;
  const label = (kind: LabelKind): JSX.Element => (
    <RecordLabel
      className={`${middle} recordLabel`}
      kind={kind}
      title={title}
      performer={performer}
      size={size}
    />
  );
  const shownFace =
    face === 'cover' ? (
      // A cover that is missing or does not load stands in as «Ямайская», as on the turntable.
      <Cover src={cover} className={middle} fallback={() => label('jamaican')} />
    ) : (
      label(face)
    );

  return (
    <div
      ref={frame}
      className="pp ppPlatter"
      style={at(part.rect)}
      hidden={!shown}
      role="img"
      aria-label={`${controlName(part.id, part.side)}, ${size}`}
      data-size={size}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerEnd}
      onLostPointerCapture={onPointerEnd}
    >
      <div className="ppDots1" />
      <div className="ppDots2" />
      <div ref={spin} className="ppSpin">
        <div
          ref={disc}
          className="ppDisc"
          data-size={size}
          data-spinning={playing && !still ? 'true' : 'false'}
        >
          {size === '33' ? (
            <>
              <div className="ppMat" />
              {shownFace}
              <div className="ppSpindle" />
            </>
          ) : (
            <>
              <div className="ppMat ppFelt" />
              <div className="ppRecord45" />
              {shownFace}
              <img
                className="ppAdapter"
                src={ADAPTER_ART[adapterFor(face)]}
                alt=""
                draggable={false}
                data-adapter={adapterFor(face)}
              />
            </>
          )}
        </div>
      </div>
      <div className="ppMark" />
    </div>
  );
}

/* ── what only shows ───────────────────────────────────────────────────────── */

/** The meter's segments: green, yellow, orange, red, as the generator's. */
function segmentColour(share: number): string {
  if (share < 0.6) return 'var(--pult-led-green)';
  if (share < 0.78) return 'var(--pult-led-yellow)';
  if (share < 0.9) return 'var(--pult-led-orange)';
  return 'var(--pult-led-red)';
}

/** How often MAIN's meter reads the level: ten times a second, while the pult is open. */
export const METER_EVERY_MS = 100;

/** The meter's scale: a level at this many dB below full lights nothing. */
const METER_FLOOR_DB = -48;

/** A peak level as the share of the meter it lights: in dB, from the floor to full. */
function meterShare(peak: number): number {
  if (!(peak > 0)) return 0;
  return Math.min(1, Math.max(0, (20 * Math.log10(peak) - METER_FLOOR_DB) / -METER_FLOOR_DB));
}

/**
 * MAIN's four columns (task 56): the level of the sound going to the speaker, read from the
 * meter's analyser {@link METER_EVERY_MS} — here, in the component, so it runs only while the pult
 * is drawn, and `engine/` keeps its rule of no `requestAnimationFrame` untouched.
 */
function Meter({
  part,
  shown,
  level,
}: {
  readonly part: Extract<PultPart, { kind: 'meter' }>;
  readonly shown: boolean;
  readonly level: () => number;
}): JSX.Element {
  const [lit, setLit] = useState(0);
  useEffect(() => {
    // Out of sight on another tab of the mixer, it reads nothing.
    if (!shown) return undefined;
    const timer = setInterval(() => {
      setLit(meterShare(level()));
    }, METER_EVERY_MS);
    return () => {
      clearInterval(timer);
    };
  }, [level, shown]);
  const { w, h } = part.rect;
  const { segments } = part;
  const gap = w * 0.1;
  const cw = (w - gap * 3) / 4;
  const sh = h / segments;
  return (
    <svg
      className="pp ppMeter"
      // An SVG has no `hidden` of its own: out of sight as the parts with it are.
      style={{ ...at(part.rect), ...(shown ? {} : { display: 'none' }) }}
      viewBox={`0 0 ${String(w)} ${String(h)}`}
      role="meter"
      aria-label="MAIN"
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(lit * 100)}
    >
      {[0, 1, 2, 3].map((column) =>
        Array.from({ length: segments }, (_, s) => (
          <rect
            key={`${String(column)}-${String(s)}`}
            x={column * (cw + gap)}
            y={h - (s + 1) * sh + sh * 0.18}
            width={cw}
            height={sh * 0.64}
            rx={sh * 0.15}
            fill={segmentColour((s + 0.5) / segments)}
            opacity={(s + 0.5) / segments <= lit ? 1 : 0.13}
            data-lit={(s + 0.5) / segments <= lit ? 'true' : undefined}
          />
        )),
      )}
    </svg>
  );
}

/* ── the board ─────────────────────────────────────────────────────────────── */

/** A part's key among the board's: its id, or what it is and where — never its place in the list. */
function partKey(part: PultPart): string {
  if ('id' in part) return part.id;
  const at =
    part.kind === 'label'
      ? `${String(part.x)},${String(part.y)}`
      : `${String(part.rect.x)},${String(part.rect.y)}`;
  const tab = part.shownOn === undefined ? '' : ` ${part.shownOn.group}.${part.shownOn.tab}`;
  return `${part.kind}@${at}${tab}`;
}

export function PultBoard({
  layout,
  lights,
  onKey,
  onKeyUp,
  onKeyHold,
  screen,
  level,
  tabs,
  onTab,
}: PultBoardProps): JSX.Element {
  const stored = usePult((s) => s.tabs);
  const chosen = tabs ?? stored;
  const choose = onTab ?? chooseTab;
  return (
    <div className="pultBoard" data-orientation={layout.orientation}>
      {layout.parts.map((part) => {
        const key = partKey(part);
        const shown = part.shownOn === undefined || chosen[part.shownOn.group] === part.shownOn.tab;
        switch (part.kind) {
          case 'label':
            return (
              <div
                key={key}
                className="ppText"
                style={{ '--x': part.x, '--y': part.y, '--fs': part.fs } as CSSProperties}
                hidden={!shown}
                aria-hidden="true"
              >
                {part.text}
              </div>
            );
          case 'key':
            return (
              <Key
                key={key}
                part={part}
                shown={shown}
                lit={lights.lit.has(part.id)}
                onKey={onKey}
                onKeyUp={onKeyUp}
                onKeyHold={onKeyHold}
              />
            );
          case 'tab':
            return (
              <Tab key={key} part={part} chosen={chosen[part.group] === part.tab} onTab={choose} />
            );
          case 'knob':
            return <Knob key={key} part={part} shown={shown} />;
          case 'fader':
            return <Fader key={key} part={part} shown={shown} />;
          case 'platter':
            return (
              <Platter
                key={key}
                part={part}
                shown={shown}
                size={lights.decks[part.side === 'B' ? 'B' : 'A'].record}
              />
            );
          case 'pad':
            return (
              <Pad
                key={key}
                part={part}
                shown={shown}
                word={padWord(lights.decks[part.side === 'B' ? 'B' : 'A'].cells, part.id)}
                lit={lights.lit.has(part.id)}
                onKey={onKey}
                onKeyUp={onKeyUp}
                onKeyHold={onKeyHold}
              />
            );
          case 'paddle':
            return <Paddle key={key} part={part} shown={shown} />;
          case 'screen':
            return (
              <div key={key} className="pp ppScreen" style={at(part.rect)} hidden={!shown}>
                {screen}
              </div>
            );
          case 'meter':
            return <Meter key={key} part={part} shown={shown} level={level} />;
          case 'letter':
            return (
              <div
                key={key}
                className="pp ppLetter"
                style={at(part.rect, { '--fs': part.fs })}
                data-deck={part.deck}
                hidden={!shown}
                aria-hidden="true"
              >
                {part.deck}
              </div>
            );
          case 'sep':
            return <div key={key} className="pp ppSep" style={at(part.rect)} aria-hidden="true" />;
        }
      })}
    </div>
  );
}
